import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import invoiceService from '../../services/invoiceService';
import InvoiceDialog from './InvoiceDialog';
import type {
  Invoice,
  InvoiceAttachment,
  InvoiceExtractionStatus,
  InvoiceExtractedData,
} from '../../types/venue';

// ── Status styles ─────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#f3f4f6', color: '#374151' },
  open:  { bg: '#dafbe1', color: '#1a7f37' },
  void:  { bg: '#fde8e8', color: '#b91c1c' },
};

const EXTRACTION_STATUS_STYLE: Record<InvoiceExtractionStatus, { bg: string; color: string; label: string }> = {
  pending:   { bg: '#dbeafe', color: '#1d4ed8', label: 'Pending' },
  completed: { bg: '#dafbe1', color: '#1a7f37', label: 'Completed' },
  failed:    { bg: '#fde8e8', color: '#b91c1c', label: 'Failed' },
};

// ── Small helpers ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoice: Invoice;
  venueId: string;
  supplierName?: string;
}

const LabelValue: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}
    >
      {label}
    </Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const mimeLabel = (mimeType: string) => {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType === 'image/jpeg') return 'JPEG';
  if (mimeType === 'image/png') return 'PNG';
  return mimeType;
};

const extractErrorMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } }; message?: string })
    ?.response?.data?.message ?? (err as Error)?.message ?? fallback;

// ── Extraction row ────────────────────────────────────────────────────────────

interface ExtractionRowProps {
  label: string;
  currentDisplay: string;
  extractedDisplay: string;
  differs: boolean;
  applyable: boolean;
  applying: boolean;
  applied: boolean;
  onApply?: () => void;
  infoTip?: string;
}

const ExtractionRow: React.FC<ExtractionRowProps> = ({
  label,
  currentDisplay,
  extractedDisplay,
  differs,
  applyable,
  applying,
  applied,
  onApply,
  infoTip,
}) => (
  <Box sx={{ display: 'contents' }}>
    {/* label */}
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', alignSelf: 'center' }}
    >
      {label}
    </Typography>

    {/* current */}
    <Typography variant="body2" sx={{ alignSelf: 'center', color: currentDisplay === '—' ? 'text.disabled' : 'inherit' }}>
      {currentDisplay}
    </Typography>

    {/* extracted + action */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
          bgcolor: differs ? '#fffbeb' : 'transparent',
          color: differs ? '#92400e' : 'inherit',
          fontWeight: differs ? 500 : 400,
        }}
      >
        {extractedDisplay}
      </Typography>

      {applyable ? (
        applied ? (
          <Typography variant="caption" color="success.main" sx={{ minWidth: 52, textAlign: 'center' }}>
            Applied
          </Typography>
        ) : (
          <Button
            size="small"
            variant="outlined"
            disabled={applying}
            onClick={onApply}
            sx={{
              textTransform: 'none',
              minWidth: 52,
              fontSize: '0.7rem',
              py: 0.25,
              px: 0.75,
              flexShrink: 0,
            }}
          >
            {applying ? <CircularProgress size={12} /> : 'Apply'}
          </Button>
        )
      ) : infoTip ? (
        <Tooltip title={infoTip} arrow>
          <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
        </Tooltip>
      ) : null}
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const InvoiceDetailPanel: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  invoice,
  venueId,
  supplierName,
}) => {
  const theme = useTheme();

  // ── Existing state ──────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  // ── Live invoice (updated by apply operations) ──────────────────────────────
  const [liveInvoice, setLiveInvoice] = useState<Invoice>(invoice);

  // ── Extraction state ────────────────────────────────────────────────────────
  const [extracting, setExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState<InvoiceExtractionStatus | null>(
    invoice.extractionStatus ?? null,
  );
  const [extractedData, setExtractedData] = useState<InvoiceExtractedData | null>(
    invoice.extractedData ?? null,
  );
  const [extractionError, setExtractionError] = useState<string | null>(
    invoice.extractionError ?? null,
  );
  const [extractionRawText, setExtractionRawText] = useState<string | null>(
    invoice.extractionRawText ?? null,
  );
  const [rawTextOpen, setRawTextOpen] = useState(false);
  const [applyingField, setApplyingField] = useState<string | null>(null);
  const [appliedFields, setAppliedFields] = useState<Set<string>>(new Set());
  const [applyError, setApplyError] = useState<string | null>(null);

  // Reset all local state when switching to a different invoice
  useEffect(() => {
    setLiveInvoice(invoice);
    setExtractionStatus(invoice.extractionStatus ?? null);
    setExtractedData(invoice.extractedData ?? null);
    setExtractionError(invoice.extractionError ?? null);
    setExtractionRawText(invoice.extractionRawText ?? null);
    setRawTextOpen(false);
    setAppliedFields(new Set());
    setApplyError(null);
    setOpenError(null);
  }, [invoice._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = STATUS_STYLE[liveInvoice.status] ?? STATUS_STYLE.draft;

  // ── Attachment open ─────────────────────────────────────────────────────────
  const handleOpenAttachment = async (attachment: InvoiceAttachment) => {
    setOpeningId(attachment._id);
    setOpenError(null);
    try {
      const url = invoiceService.getAttachmentUrl(venueId, liveInvoice._id, attachment._id);
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: attachment.mimeType });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err: unknown) {
      setOpenError(extractErrorMsg(err, 'Failed to open file'));
    } finally {
      setOpeningId(null);
    }
  };

  // ── Extraction ──────────────────────────────────────────────────────────────
  const handleExtract = async () => {
    setExtracting(true);
    setApplyError(null);
    try {
      const result = await invoiceService.extractInvoice(venueId, liveInvoice._id);
      setExtractionStatus(result.extractionStatus);
      setExtractedData(result.extractedData);
      setExtractionError(result.extractionError ?? null);
      setAppliedFields(new Set()); // reset applied marks on fresh extraction
    } catch (err: unknown) {
      setExtractionStatus('failed');
      setExtractionError(extractErrorMsg(err, 'Extraction request failed'));
    } finally {
      setExtracting(false);
    }
  };

  // ── Apply a single field ────────────────────────────────────────────────────
  const handleApplyField = async (
    fieldKey: string,
    patch: Parameters<typeof invoiceService.updateInvoice>[2],
  ) => {
    setApplyingField(fieldKey);
    setApplyError(null);
    try {
      const updated = await invoiceService.updateInvoice(venueId, liveInvoice._id, patch);
      setLiveInvoice(updated);
      setAppliedFields(prev => new Set(prev).add(fieldKey));
      onSuccess();
    } catch (err: unknown) {
      setApplyError(extractErrorMsg(err, 'Failed to apply field'));
    } finally {
      setApplyingField(null);
    }
  };

  // ── Build extraction rows ───────────────────────────────────────────────────
  const buildRows = () => {
    if (!extractedData) return [];

    const currentTotal = liveInvoice.totalAmount != null
      ? liveInvoice.totalAmount.toFixed(2)
      : '—';
    const extractedTotal = extractedData.totalAmount != null
      ? extractedData.totalAmount.toFixed(2)
      : null;

    const rows: Array<{
      key: string;
      label: string;
      currentDisplay: string;
      extractedDisplay: string | null | undefined;
      applyable: boolean;
      infoTip?: string;
      patch?: Parameters<typeof invoiceService.updateInvoice>[2];
    }> = [
      {
        key: 'invoiceNumber',
        label: 'Invoice #',
        currentDisplay: liveInvoice.invoiceNumber,
        extractedDisplay: extractedData.invoiceNumber,
        applyable: true,
        patch: extractedData.invoiceNumber ? { invoiceNumber: extractedData.invoiceNumber } : undefined,
      },
      {
        key: 'supplierName',
        label: 'Supplier',
        currentDisplay: supplierName ?? '—',
        extractedDisplay: extractedData.supplierName,
        applyable: false,
        infoTip: 'Supplier is managed separately and cannot be changed here.',
      },
      {
        key: 'invoiceDate',
        label: 'Invoice Date',
        currentDisplay: formatDate(liveInvoice.invoiceDate),
        extractedDisplay: extractedData.invoiceDate,
        applyable: true,
        patch: extractedData.invoiceDate ? { invoiceDate: extractedData.invoiceDate } : undefined,
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        currentDisplay: formatDate(liveInvoice.dueDate),
        extractedDisplay: extractedData.dueDate,
        applyable: true,
        patch: extractedData.dueDate ? { dueDate: extractedData.dueDate } : undefined,
      },
      {
        key: 'totalAmount',
        label: 'Total Amount',
        currentDisplay: currentTotal,
        extractedDisplay: extractedTotal,
        applyable: true,
        patch: extractedData.totalAmount != null ? { totalAmount: extractedData.totalAmount } : undefined,
      },
      {
        key: 'currency',
        label: 'Currency',
        currentDisplay: liveInvoice.currencyCode ?? '—',
        extractedDisplay: extractedData.currency,
        applyable: true,
        patch: extractedData.currency ? { currencyCode: extractedData.currency } : undefined,
      },
    ];

    return rows.filter(r => r.extractedDisplay != null && r.extractedDisplay !== '');
  };

  const extractionRows = buildRows();

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open && !editOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
              {liveInvoice.invoiceNumber}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 1, py: 0.25,
                borderRadius: 1,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                backgroundColor: style.bg,
                color: style.color,
              }}
            >
              {liveInvoice.status}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── Invoice details ─────────────────────────────────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <LabelValue label="Supplier">
              <Typography variant="body2">
                {supplierName ?? <span style={{ color: theme.palette.text.disabled }}>—</span>}
              </Typography>
            </LabelValue>

            <LabelValue label="Total Amount">
              <Typography variant="body2">
                {liveInvoice.totalAmount != null
                  ? `${liveInvoice.currencyCode ? liveInvoice.currencyCode + ' ' : ''}${liveInvoice.totalAmount.toFixed(2)}`
                  : '—'}
              </Typography>
            </LabelValue>

            <LabelValue label="Invoice Date">
              <Typography variant="body2">{formatDate(liveInvoice.invoiceDate)}</Typography>
            </LabelValue>

            <LabelValue label="Due Date">
              <Typography variant="body2">{formatDate(liveInvoice.dueDate)}</Typography>
            </LabelValue>
          </Box>

          {/* ── Attachments ─────────────────────────────────────────────────── */}
          <Divider />
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Attachments
            </Typography>

            {openError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setOpenError(null)}>
                {openError}
              </Alert>
            )}

            {liveInvoice.attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No attachments.</Typography>
            ) : (
              <List dense disablePadding>
                {liveInvoice.attachments.map(a => (
                  <ListItem
                    key={a._id}
                    disableGutters
                    secondaryAction={
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAttachment(a)}
                        disabled={openingId === a._id}
                        title="Open file"
                      >
                        {openingId === a._id
                          ? <CircularProgress size={16} />
                          : <OpenInNewIcon fontSize="small" />}
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={a.originalName}
                      secondary={`${formatFileSize(a.sizeBytes)} · ${mimeLabel(a.mimeType)}`}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { maxWidth: 320 } }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* ── Read Invoice (extraction) ────────────────────────────────────── */}
          <Divider />
          <Box>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Read Invoice
              </Typography>

              {extractionStatus && (() => {
                const s = EXTRACTION_STATUS_STYLE[extractionStatus];
                return (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      px: 0.75, py: 0.15,
                      borderRadius: 0.75,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      bgcolor: s.bg,
                      color: s.color,
                    }}
                  >
                    {s.label}
                  </Box>
                );
              })()}

              <Button
                size="small"
                variant="outlined"
                disabled={extracting || liveInvoice.attachments.length === 0}
                onClick={handleExtract}
                startIcon={extracting ? <CircularProgress size={13} /> : undefined}
                sx={{ textTransform: 'none', ml: 'auto', flexShrink: 0 }}
              >
                {extracting ? 'Reading…' : 'Read Invoice'}
              </Button>
            </Box>

            {/* No-attachment hint */}
            {liveInvoice.attachments.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Upload an attachment first to enable invoice reading.
              </Typography>
            )}

            {/* Extraction failed */}
            {extractionStatus === 'failed' && extractionError && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {extractionError}
              </Alert>
            )}

            {/* Apply error */}
            {applyError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApplyError(null)}>
                {applyError}
              </Alert>
            )}

            {/* Comparison table */}
            {extractionStatus === 'completed' && extractionRows.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr 1fr',
                  columnGap: 1.5,
                  rowGap: 1,
                  alignItems: 'center',
                }}
              >
                {/* Column headers */}
                <Box /> {/* label column — no header */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: '0.04em' }}>
                  CURRENT
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: '0.04em' }}>
                  EXTRACTED
                </Typography>

                {/* Data rows */}
                {extractionRows.map(row => (
                  <ExtractionRow
                    key={row.key}
                    label={row.label}
                    currentDisplay={row.currentDisplay}
                    extractedDisplay={row.extractedDisplay!}
                    differs={row.currentDisplay !== row.extractedDisplay}
                    applyable={row.applyable}
                    applying={applyingField === row.key}
                    applied={appliedFields.has(row.key)}
                    onApply={row.patch ? () => handleApplyField(row.key, row.patch!) : undefined}
                    infoTip={row.infoTip}
                  />
                ))}
              </Box>
            )}

            {/* No fields extracted */}
            {extractionStatus === 'completed' && extractionRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No fields could be extracted from this document.
              </Typography>
            )}

            {/* Raw text toggle */}
            {extractionRawText && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => setRawTextOpen(v => !v)}
                  sx={{ textTransform: 'none', p: 0, fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  {rawTextOpen ? '▲ Hide raw text' : '▶ Show raw text'}
                </Button>
                <Collapse in={rawTextOpen}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: '#f9fafb',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}
                    >
                      {extractionRawText}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>

          {/* ── Linked entities ──────────────────────────────────────────────── */}
          {(liveInvoice.purchaseOrderId || liveInvoice.goodsReceiptIds.length > 0) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Linked Entities
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {liveInvoice.purchaseOrderId && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Purchase Order
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.82rem">
                        {liveInvoice.purchaseOrderId.slice(-8).toUpperCase()}
                      </Typography>
                    </Box>
                  )}
                  {liveInvoice.goodsReceiptIds.map(grId => (
                    <Box key={grId} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Goods Receipt
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.82rem">
                        {grId.slice(-8).toUpperCase()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* ── Notes ───────────────────────────────────────────────────────── */}
          {liveInvoice.notes && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {liveInvoice.notes}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog — mounts on top; panel hides itself while edit is open */}
      {editOpen && (
        <InvoiceDialog
          open
          onClose={() => setEditOpen(false)}
          onSuccess={() => { onSuccess(); onClose(); }}
          venueId={venueId}
          purchaseOrderId={liveInvoice.purchaseOrderId ?? ''}
          supplierId={liveInvoice.supplierId}
          invoice={liveInvoice}
        />
      )}
    </>
  );
};

export default InvoiceDetailPanel;
